import React, { useState } from 'react'
import { X } from 'lucide-react'
import { Input } from './ui/input'
import { sports } from '../config'

interface SportsMultiSelectProps {
  label: string
  description?: string
  selectedSportIds: number[]
  onSportsChange: (sportIds: number[]) => void
  placeholder?: string
}

const SportsMultiSelect: React.FC<SportsMultiSelectProps> = ({
  label,
  description,
  selectedSportIds,
  onSportsChange,
  placeholder = 'Add a sport...',
}) => {
  const [inputValue, setInputValue] = useState('')
  const [showSuggestions, setShowSuggestions] = useState(false)

  // Get selected sports objects
  const selectedSports = sports.filter((sport) => selectedSportIds.includes(sport.id))

  // Get available sports (not already selected) and filter by input
  const availableSports = sports.filter(
    (sport) =>
      !selectedSportIds.includes(sport.id) &&
      sport.name.toLowerCase().includes(inputValue.toLowerCase()),
  )

  const handleAddSport = (sportId: number) => {
    if (!selectedSportIds.includes(sportId)) {
      onSportsChange([...selectedSportIds, sportId])
    }
    setInputValue('')
    setShowSuggestions(false)
  }

  const handleRemoveSport = (sportId: number) => {
    onSportsChange(selectedSportIds.filter((id) => id !== sportId))
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      // If there's an exact match, add it
      const exactMatch = availableSports.find(
        (sport) => sport.name.toLowerCase() === inputValue.toLowerCase(),
      )
      if (exactMatch) {
        handleAddSport(exactMatch.id)
      }
    } else if (e.key === 'Escape') {
      setShowSuggestions(false)
    }
  }

  return (
    <div className="space-y-2">
      <div>
        <label className="text-sm font-medium text-white">{label}</label>
        {description && <p className="text-sm text-gray-400">{description}</p>}
      </div>

      <div className="flex flex-wrap gap-2 mb-2">
        {selectedSports.map((sport) => (
          <div
            key={sport.id}
            className="flex items-center bg-[#C0FD72]/20 text-[#C0FD72] border border-[#C0FD72]/30 rounded-full px-3 py-1 text-sm"
          >
            <span>{sport.name}</span>
            <button
              type="button"
              onClick={() => handleRemoveSport(sport.id)}
              className="ml-1 p-0.5 rounded-full hover:bg-[#C0FD72]/30 focus:outline-hidden"
            >
              <X className="h-3 w-3" />
            </button>
          </div>
        ))}
      </div>

      <div className="space-y-3">
        <Input
          type="text"
          placeholder={placeholder}
          value={inputValue}
          onChange={(e) => {
            setInputValue(e.target.value)
            setShowSuggestions(true)
          }}
          onFocus={() => setShowSuggestions(true)}
          onBlur={() => {
            // Delay hiding suggestions to allow for clicks
            setTimeout(() => setShowSuggestions(false), 150)
          }}
          onKeyDown={handleKeyDown}
          className="bg-white/5 border-white/10 text-white placeholder:text-gray-400"
        />

        {showSuggestions && availableSports.length > 0 && (
          <div className="space-y-2">
            <p className="text-xs text-gray-400">Available sports:</p>
            <div className="flex flex-wrap gap-2">
              {availableSports.slice(0, 8).map((sport) => (
                <button
                  key={sport.id}
                  className="px-3 py-1 text-sm bg-white/5 hover:bg-white/10 text-gray-300 hover:text-white border border-white/10 hover:border-white/20 rounded-full transition-colors focus:outline-hidden focus:ring-2 focus:ring-[#C0FD72]/50"
                  onMouseDown={(e) => {
                    e.preventDefault() // Prevent blur before click
                    handleAddSport(sport.id)
                  }}
                >
                  {sport.name}
                </button>
              ))}
              {availableSports.length > 8 && (
                <span className="px-3 py-1 text-xs text-gray-500 self-center">
                  Type to search for more
                </span>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default SportsMultiSelect
